/**
 * m3 echo Frontend - Optimization Hooks (V3.0)
 *
 * React Query hooks for optimization test data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import {
  OptimizationTest,
  OptimizationTestDetail,
  CreateOptimizationTestRequest,
  UpdateProjectHomepageRequest,
  CombinedTimeline,
} from '../types/api';
import { projectKeys } from './useProjects';
import { billingApi } from '../services/billing';
import { getIdentityScope } from '../stores/useAuthStore';

// ===========================
// Query Keys
// ===========================

// Scoped to the active identity so no cache entry survives an account or tenant
// switch. `all` stays unscoped for broad invalidations.
export const optimizationKeys = {
  all: ['optimization'] as const,
  scoped: () => [...optimizationKeys.all, getIdentityScope()] as const,
  lists: () => [...optimizationKeys.scoped(), 'list'] as const,
  list: (projectId: number) => [...optimizationKeys.lists(), projectId] as const,
  details: () => [...optimizationKeys.scoped(), 'detail'] as const,
  detail: (projectId: number, testId: number) => [...optimizationKeys.details(), projectId, testId] as const,
  combinedTimeline: (projectId: number) => [...optimizationKeys.scoped(), 'combined-timeline', projectId] as const,
};

// Effective billing config of the current tenant. The identity scope is appended
// so that the ['billing', 'my-config'] prefix used elsewhere still matches.
const myBillingConfigKey = () => ['billing', 'my-config', getIdentityScope()] as const;

// ===========================
// Queries
// ===========================

/**
 * Hook to fetch all optimization tests for a project
 */
export function useOptimizationTests(projectId: number) {
  const query = useQuery({
    queryKey: optimizationKeys.list(projectId),
    queryFn: async () => {
      const response = await apiClient.get<OptimizationTest[]>(
        `/optimization/projects/${projectId}/tests`
      );
      return response.data;
    },
    enabled: !!projectId,
    // Poll every 3 seconds if there are pending/running tests
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActiveTests = data?.some(
        test => test.status === 'pending' || test.status === 'running'
      );
      return hasActiveTests ? 3000 : false;
    },
  });

  return query;
}

/**
 * Hook to fetch a single optimization test detail
 */
export function useOptimizationTestDetail(projectId: number, testId: number) {
  return useQuery({
    queryKey: optimizationKeys.detail(projectId, testId),
    queryFn: async () => {
      const response = await apiClient.get<OptimizationTestDetail>(
        `/optimization/projects/${projectId}/tests/${testId}`
      );
      return response.data;
    },
    enabled: !!projectId && !!testId,
  });
}

/**
 * Hook to fetch combined timeline (LLM + Optimization tests)
 */
export function useCombinedTimeline(projectId: number) {
  return useQuery({
    queryKey: optimizationKeys.combinedTimeline(projectId),
    queryFn: async () => {
      const response = await apiClient.get<CombinedTimeline>(
        `/optimization/projects/${projectId}/combined-timeline`
      );
      return response.data;
    },
    enabled: !!projectId,
    // Poll every 5 seconds if there are pending/running items
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActiveItems = data?.items.some(
        item => item.status === 'pending' || item.status === 'running'
      );
      return hasActiveItems ? 5000 : false;
    },
  });
}

// ===========================
// Quota Hook
// ===========================

/**
 * Hook to fetch optimization quota info from billing config
 */
export function useOptimizationQuota() {
  const { data, ...rest } = useQuery({
    queryKey: myBillingConfigKey(),
    queryFn: async () => {
      const response = await billingApi.getMyConfig();
      return response.data;
    },
    staleTime: 30_000,
  });

  return {
    runQuota: data?.optimization_run_quota ?? 0,
    runsUsed: data?.optimization_runs_used ?? 0,
    runsRemaining: data?.optimization_runs_remaining ?? 0,
    quotaExhausted: (data?.optimization_runs_remaining ?? 0) <= 0,
    hasModule: !!data?.active_features?.['show_benchmark_tab'],
    ...rest,
  };
}

// ===========================
// Mutations
// ===========================

/**
 * Hook to create a new optimization test
 */
export function useCreateOptimizationTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: number;
      data: CreateOptimizationTestRequest;
    }) => {
      const response = await apiClient.post<OptimizationTest>(
        `/optimization/projects/${projectId}/tests`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate optimization tests list
      queryClient.invalidateQueries({
        queryKey: optimizationKeys.list(variables.projectId),
      });
      // Invalidate combined timeline
      queryClient.invalidateQueries({
        queryKey: optimizationKeys.combinedTimeline(variables.projectId),
      });
      // Invalidate billing config so quota card updates immediately
      queryClient.invalidateQueries({
        queryKey: myBillingConfigKey(),
      });
    },
  });
}

/**
 * Hook to create a re-test of an existing optimization test
 */
export function useRetestOptimization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      testId,
    }: {
      projectId: number;
      testId: number;
    }) => {
      const response = await apiClient.post<OptimizationTest>(
        `/optimization/projects/${projectId}/tests/${testId}/retest`
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate optimization tests list
      queryClient.invalidateQueries({
        queryKey: optimizationKeys.list(variables.projectId),
      });
      // Invalidate combined timeline
      queryClient.invalidateQueries({
        queryKey: optimizationKeys.combinedTimeline(variables.projectId),
      });
      // Invalidate billing config so quota card updates immediately
      queryClient.invalidateQueries({
        queryKey: myBillingConfigKey(),
      });
    },
  });
}

/**
 * Hook to update project homepage URL
 */
export function useUpdateProjectHomepage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: number;
      data: UpdateProjectHomepageRequest;
    }) => {
      const response = await apiClient.patch(
        `/projects/${projectId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate project queries to refetch with updated homepage_url
      queryClient.invalidateQueries({
        queryKey: projectKeys.detail(variables.projectId),
      });
      // Also invalidate the project list queries
      queryClient.invalidateQueries({
        queryKey: projectKeys.lists(),
      });
    },
  });
}
